// One small-multiples tile: name + total in the header, AreaCurve in the
// body, delta label + cross-attribution footer. Clicking the tile fires
// `onSelect` so the parent page can drill the rest of the SPA into that
// entity (country/actor/family).
import type { MouseEventHandler } from 'react';
import AreaCurve from '../charts/AreaCurve';
import type { PulseTile as TilePayload } from '../types';

interface Props {
  tile: TilePayload;
  color: string;
  onSelect?: (tile: TilePayload) => void;
}

function formatTotal(n: number): string {
  if (n >= 10000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function deltaTone(delta: number | null): 'up' | 'down' | 'flat' {
  if (delta == null) return 'up';
  if (delta > 5) return 'up';
  if (delta < -5) return 'down';
  return 'flat';
}

export default function PulseTile({ tile, color, onSelect }: Props) {
  const ariaLabel = `${tile.name}: ${tile.total} mentions, ${tile.delta_label}`;
  const tone = deltaTone(tile.delta_pct);

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    if (onSelect) onSelect(tile);
  };

  return (
    <button
      type="button"
      className="pulse-tile"
      onClick={handleClick}
      aria-label={`Drill into ${tile.name}`}
      style={{ ['--tile-accent' as string]: color }}
    >
      <header className="pulse-tile-head">
        <div className="pulse-tile-title">
          {tile.flag && (
            <span className="pulse-tile-flag" aria-hidden>
              {tile.flag}
            </span>
          )}
          <span className="pulse-tile-name">{tile.name}</span>
        </div>
        <div className="pulse-tile-total">
          <strong>{formatTotal(tile.total)}</strong>
          <span>mentions</span>
        </div>
      </header>
      <div className="pulse-tile-chart">
        <AreaCurve data={tile.series} color={color} ariaLabel={ariaLabel} />
      </div>
      <footer className="pulse-tile-foot">
        <div className={`pulse-tile-delta pulse-tile-delta-${tone}`}>
          {tile.delta_label}
        </div>
        <TileSubLine tile={tile} />
      </footer>
    </button>
  );
}

function TileSubLine({ tile }: { tile: TilePayload }) {
  const sub = tile.sub ?? {};
  const parts: string[] = [];
  if (tile.type === 'country') {
    if (sub.top_actor) parts.push(`Top actor ${sub.top_actor}`);
    if (sub.top_family) parts.push(`Top family ${sub.top_family}`);
  } else if (tile.type === 'actor') {
    if (sub.top_family) parts.push(`Top family ${sub.top_family}`);
    if (sub.country_label) {
      const extra = sub.country_extra ? ` +${sub.country_extra}` : '';
      parts.push(`${sub.country_label}${extra}`);
    }
  } else if (tile.type === 'family') {
    if (sub.top_actor) parts.push(`Top actor ${sub.top_actor}`);
    if (sub.country_badge_label) parts.push(`Linked to ${sub.country_badge_label}`);
  }
  if (parts.length === 0) {
    return <div className="pulse-tile-sub pulse-tile-sub-empty">No cross-attribution</div>;
  }
  return (
    <div className="pulse-tile-sub">
      {parts.map((p, i) => (
        <span key={i} className="pulse-tile-sub-line">
          {p}
        </span>
      ))}
    </div>
  );
}
